import { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { registerQueue, resetRegistration, getMaxOrderIndex } from '../redux/slices/queueSlice';
import { showAlert } from '../redux/slices/uiSlice';
import {
  gregorianToLunar,
  lunarToGregorian,
  autoFillDates,
  autoConvertToMinguo,
  convertMinguoForStorage
} from '../utils/calendarConverter';

// 表單初始值
const initialFormData = {
  email: '',
  name: '',
  phone: '',
  gender: 'male',
  birthYear: '',
  birthMonth: '',
  birthDay: '',
  // Change C / 階段 2.6（擴大範圍補修）：useRegistrationForm default 改 lunar
  // 全系統生日只填農曆；BirthdayPicker 已 default lunarOnly=true 強制鎖農曆，
  // 但 state 仍保留 calendarType 欄位（不刪），讓既存讀取邏輯與 BasicInfoSection 顯示模式不破壞
  calendarType: 'lunar',
  lunarIsLeapMonth: false,
  addresses: [{ address: '', addressType: 'home' }],
  familyMembers: [],
  consultationTopics: [],
  otherDetails: '',
  remarks: ''
};

export const useRegistrationForm = (embedded = false) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  
  const { 
    isLoading, 
    registeredQueueNumber, 
    waitingCount, 
    estimatedWaitTime, 
    estimatedEndTime, 
    error, 
    maxOrderIndex, 
    maxOrderMessage,
    registeredOrderIndex
  } = useSelector((state) => state.queue);

  const [formData, setFormData] = useState(initialFormData);
  const [formErrors, setFormErrors] = useState({});
  const [showSuccessPage, setShowSuccessPage] = useState(false);

  // 組件掛載時先重置狀態並獲取最大叫號順序
  useEffect(() => {
    dispatch(resetRegistration());
    dispatch(getMaxOrderIndex());
    setFormData(initialFormData);
    setShowSuccessPage(false);
  }, [dispatch]);

  // 如果已經登記成功，則顯示成功頁面
  useEffect(() => {
    if (registeredQueueNumber) {
      setShowSuccessPage(true);
    }
  }, [registeredQueueNumber]);

  // 處理表單錯誤
  useEffect(() => {
    if (error) {
      dispatch(showAlert({
        message: error,
        severity: 'error'
      }));
    }
  }, [error, dispatch]);

  // 自動轉換日期的功能函數
  const autoConvertDate = useCallback((newFormData) => {
    if (newFormData.birthYear && newFormData.birthMonth && newFormData.birthDay) {
      const inputYear = parseInt(newFormData.birthYear);
      const month = parseInt(newFormData.birthMonth);
      const day = parseInt(newFormData.birthDay);

      if (inputYear && month && day) {
        try {
          if (newFormData.calendarType === 'gregorian') {
            // 國曆：先轉換為西元年，再進行轉換
            const { minguoYear } = autoConvertToMinguo(inputYear);
            const gregorianYear = convertMinguoForStorage(minguoYear);
            const lunarDate = gregorianToLunar(gregorianYear, month, day);
            if (lunarDate) {
              newFormData.convertedLunarYear = lunarDate.year;
              newFormData.convertedLunarMonth = lunarDate.month;
              newFormData.convertedLunarDay = lunarDate.day;
              newFormData.convertedLunarIsLeapMonth = lunarDate.isLeapMonth;
            }
          } else {
            // 農曆：先轉換為西元年，再進行轉換
            const { minguoYear } = autoConvertToMinguo(inputYear);
            const lunarYear = convertMinguoForStorage(minguoYear);
            const gregorianDate = lunarToGregorian(lunarYear, month, day, newFormData.lunarIsLeapMonth);
            if (gregorianDate) {
              newFormData.convertedGregorianYear = gregorianDate.year;
              newFormData.convertedGregorianMonth = gregorianDate.month;
              newFormData.convertedGregorianDay = gregorianDate.day;
            }
          }
        } catch (error) {
          console.error('日期轉換錯誤:', error);
        }
      }
    }
    return newFormData;
  }, []);

  // 家人自動轉換日期
  const autoConvertFamilyMemberDate = useCallback((member) => {
    if (member.birthYear && member.birthMonth && member.birthDay) {
      const inputYear = parseInt(member.birthYear);
      const month = parseInt(member.birthMonth);
      const day = parseInt(member.birthDay);

      if (inputYear && month && day) {
        try {
          if (member.calendarType === 'gregorian') {
            // 國曆：先轉換為西元年，再進行轉換
            const { minguoYear } = autoConvertToMinguo(inputYear);
            const gregorianYear = convertMinguoForStorage(minguoYear);
            const lunarDate = gregorianToLunar(gregorianYear, month, day);
            if (lunarDate) {
              member.convertedLunarYear = lunarDate.year;
              member.convertedLunarMonth = lunarDate.month;
              member.convertedLunarDay = lunarDate.day;
              member.convertedLunarIsLeapMonth = lunarDate.isLeapMonth;
            }
          } else {
            // 農曆：先轉換為西元年，再進行轉換
            const { minguoYear } = autoConvertToMinguo(inputYear);
            const lunarYear = convertMinguoForStorage(minguoYear);
            const gregorianDate = lunarToGregorian(lunarYear, month, day, member.lunarIsLeapMonth);
            if (gregorianDate) {
              member.convertedGregorianYear = gregorianDate.year;
              member.convertedGregorianMonth = gregorianDate.month;
              member.convertedGregorianDay = gregorianDate.day;
            }
          }
        } catch (error) {
          console.error('家人日期轉換錯誤:', error);
        }
      }
    }
    return member;
  }, []);

  // 處理表單變更
  // 支援兩種簽名：
  //   handleChange(name, value)           — 單一欄位
  //   handleChange({ field1, field2... }) — 批次更新（BirthdayPicker 用）
  const handleChange = useCallback((nameOrPatch, value) => {
    // 批次更新模式
    if (typeof nameOrPatch === 'object' && nameOrPatch !== null) {
      setFormData(prev => {
        const newFormData = { ...prev, ...nameOrPatch };
        autoConvertDate(newFormData);
        return newFormData;
      });
      return;
    }

    const name = nameOrPatch;
    const newFormData = { ...formData, [name]: value };
    
    // 如果是曆法類型改變，清除轉換結果
    if (name === 'calendarType') {
      delete newFormData.convertedLunarYear;
      delete newFormData.convertedLunarMonth;
      delete newFormData.convertedLunarDay;
      delete newFormData.convertedLunarIsLeapMonth;
      delete newFormData.convertedGregorianYear;
      delete newFormData.convertedGregorianMonth;
      delete newFormData.convertedGregorianDay;
    }

    // 如果是日期相關欄位，進行自動轉換
    if (['birthYear', 'birthMonth', 'birthDay', 'calendarType', 'lunarIsLeapMonth'].includes(name)) {
      autoConvertDate(newFormData);
    }

    setFormData(newFormData);
    
    // 清除對應的錯誤
    if (formErrors[name]) {
      setFormErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  }, [formData, formErrors, autoConvertDate]);

  // 處理地址變更
  const handleAddressChange = useCallback((index, field, value) => {
    const newAddresses = [...formData.addresses];
    newAddresses[index] = {
      ...newAddresses[index],
      [field]: value
    };
    setFormData(prev => ({
      ...prev,
      addresses: newAddresses
    }));

    // 清除對應的錯誤
    const errorKey = `addresses.${index}.${field}`;
    if (formErrors[errorKey]) {
      setFormErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[errorKey];
        return newErrors;
      });
    }
  }, [formData.addresses, formErrors]);

  // 新增地址
  const addAddress = useCallback(() => {
    if (formData.addresses.length < 3) {
      setFormData(prev => ({
        ...prev,
        addresses: [...prev.addresses, { address: '', addressType: 'home' }]
      }));
    }
  }, [formData.addresses.length]);

  // 移除地址
  const removeAddress = useCallback((index) => {
    if (formData.addresses.length > 1) {
      const newAddresses = formData.addresses.filter((_, i) => i !== index);
      setFormData(prev => ({
        ...prev,
        addresses: newAddresses
      }));
    }
  }, [formData.addresses]);

  // 處理家人資料變更
  // 支援兩種簽名：
  //   handleFamilyMemberChange(index, field, value)   — 單一欄位
  //   handleFamilyMemberChange(index, patch)          — 批次更新（BirthdayPicker 用）
  const handleFamilyMemberChange = useCallback((index, fieldOrPatch, value) => {
    // 批次更新模式
    if (typeof fieldOrPatch === 'object' && fieldOrPatch !== null) {
      setFormData(prev => {
        const newFamilyMembers = [...prev.familyMembers];
        newFamilyMembers[index] = { ...newFamilyMembers[index], ...fieldOrPatch };
        autoConvertFamilyMemberDate(newFamilyMembers[index]);
        return { ...prev, familyMembers: newFamilyMembers };
      });
      return;
    }

    const field = fieldOrPatch;
    const newFamilyMembers = [...formData.familyMembers];
    newFamilyMembers[index] = {
      ...newFamilyMembers[index],
      [field]: value
    };

    // 如果是曆法類型改變，清除轉換結果
    if (field === 'calendarType') {
      delete newFamilyMembers[index].convertedLunarYear;
      delete newFamilyMembers[index].convertedLunarMonth;
      delete newFamilyMembers[index].convertedLunarDay;
      delete newFamilyMembers[index].convertedLunarIsLeapMonth;
      delete newFamilyMembers[index].convertedGregorianYear;
      delete newFamilyMembers[index].convertedGregorianMonth;
      delete newFamilyMembers[index].convertedGregorianDay;
    }

    // 如果是日期相關欄位，進行自動轉換
    if (['birthYear', 'birthMonth', 'birthDay', 'calendarType', 'lunarIsLeapMonth'].includes(field)) {
      autoConvertFamilyMemberDate(newFamilyMembers[index]);
    }

    setFormData(prev => ({
      ...prev,
      familyMembers: newFamilyMembers
    }));

    // 清除對應的錯誤
    const errorKey = `familyMembers.${index}.${field}`;
    if (formErrors[errorKey]) {
      setFormErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[errorKey];
        return newErrors;
      });
    }
  }, [formData.familyMembers, formErrors, autoConvertFamilyMemberDate]);

  // 新增家人
  const addFamilyMember = useCallback(() => {
    if (formData.familyMembers.length < 3) {
      setFormData(prev => ({
        ...prev,
        familyMembers: [
          ...prev.familyMembers,
          {
            name: '',
            gender: 'male',
            birthYear: '',
            birthMonth: '',
            birthDay: '',
            // Change C / 階段 2.6（擴大範圍補修）：家人 default 改 lunar，跟主客戶一致
            calendarType: 'lunar',
            lunarIsLeapMonth: false,
            address: '',
            addressType: 'home'
          }
        ]
      }));
    }
  }, [formData.familyMembers.length]);

  // 移除家人
  const removeFamilyMember = useCallback((index) => {
    const newFamilyMembers = formData.familyMembers.filter((_, i) => i !== index);
    setFormData(prev => ({
      ...prev,
      familyMembers: newFamilyMembers
    }));
  }, [formData.familyMembers]);

  // 表單驗證
  const validateForm = useCallback(() => {
    const errors = {};

    // 基本資料驗證
    // 信箱不做格式驗證（2026-06-21 懷特要求拿掉信箱驗證）
    
    if (!formData.name) {
      errors.name = '請輸入姓名';
    }
    
    if (!formData.phone) {
      errors.phone = '請輸入聯絡手機';
    } else if (!/^[\d-+()]{8,}$/.test(formData.phone)) {
      errors.phone = '請輸入有效的聯絡手機';
    }

    // Follow-up patch #3（OpenSpec 2026-05-23-followup-patches D3）：
    // 農曆生日三欄位必填驗證，提示文案對齊「農曆生日」
    // （BirthdayPicker 改為下拉，值為 number 或 ''；lunarOnly default true）
    if (!formData.birthYear) {
      errors.birthYear = '請選擇農曆生日年份';
    }
    if (!formData.birthMonth) {
      errors.birthMonth = '請選擇農曆生日月份';
    }
    if (!formData.birthDay) {
      errors.birthDay = '請選擇農曆生日日期';
    }

    // 地址驗證
    formData.addresses.forEach((addr, index) => {
      if (!addr.address) {
        errors[`addresses.${index}.address`] = '請輸入地址';
      }
    });

    // 家人資料驗證
    formData.familyMembers.forEach((member, index) => {
      if (!member.name) {
        errors[`familyMembers.${index}.name`] = '請輸入家人姓名';
      }
      if (!member.gender) {
        errors[`familyMembers.${index}.gender`] = '請選擇性別';
      }
      // Follow-up patch #3：家人農曆生日必填、提示對齊「農曆生日」
      if (!member.birthYear) {
        errors[`familyMembers.${index}.birthYear`] = '請選擇農曆生日年份';
      }
      if (!member.birthMonth) {
        errors[`familyMembers.${index}.birthMonth`] = '請選擇農曆生日月份';
      }
      if (!member.birthDay) {
        errors[`familyMembers.${index}.birthDay`] = '請選擇農曆生日日期';
      }
      if (!member.address) {
        errors[`familyMembers.${index}.address`] = '請輸入地址';
      }
    });

    // 請示內容驗證
    if (formData.consultationTopics.length === 0) {
      errors.consultationTopics = '請至少選擇一個請示內容';
    }

    // 其他詳細內容驗證
    if (formData.consultationTopics.includes('other') && !formData.otherDetails.trim()) {
      errors.otherDetails = '選擇「其他」時，請詳細說明您的問題';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  }, [formData]);

  // 提交表單
  const handleSubmit = useCallback(async () => {
    if (!validateForm()) {
      dispatch(showAlert({
        message: '請檢查並修正表單中的錯誤',
        severity: 'error'
      }));
      return;
    }

    try {
      // 準備提交數據
      const submissionData = {
        email: formData.email || '',
        name: formData.name,
        phone: formData.phone,
        gender: formData.gender,
        addresses: formData.addresses,
        familyMembers: formData.familyMembers,
        consultationTopics: formData.consultationTopics,
        otherDetails: formData.otherDetails || '',
        remarks: formData.remarks || ''
      };

      // Change C / 階段 2.6（擴大範圍補修）：提交分支簡化 — 移除 gregorian 分支，只走 lunar
      // 全系統生日只填農曆，BirthdayPicker default lunarOnly=true 已強制鎖農曆
      // Follow-up patch B1A：前端送民國年對齊後端 lunarToGregorian 民國年版
      // formData.birthYear 由 BirthdayPicker（getBirthYearOptions value）拿到的是西元年，
      // 透過 autoConvertToMinguo 轉成民國年再送，避免後端把西元年當民國年再 +1911 算出廢資料。
      const mainMinguo = autoConvertToMinguo(parseInt(formData.birthYear)).minguoYear;
      submissionData.lunarBirthYear = mainMinguo;
      submissionData.lunarBirthMonth = parseInt(formData.birthMonth);
      submissionData.lunarBirthDay = parseInt(formData.birthDay);
      submissionData.lunarIsLeapMonth = formData.lunarIsLeapMonth || false;

      // Change C / 階段 2.6：家人提交也只走 lunar
      // Follow-up patch B1A：家人 lunarBirthYear 同樣送民國年
      submissionData.familyMembers = submissionData.familyMembers.map(member => {
        const processedMember = { ...member };

        const memberMinguo = autoConvertToMinguo(parseInt(member.birthYear)).minguoYear;
        processedMember.lunarBirthYear = memberMinguo;
        processedMember.lunarBirthMonth = parseInt(member.birthMonth);
        processedMember.lunarBirthDay = parseInt(member.birthDay);
        processedMember.lunarIsLeapMonth = member.lunarIsLeapMonth || false;

        // 移除臨時欄位
        delete processedMember.birthYear;
        delete processedMember.birthMonth;
        delete processedMember.birthDay;
        delete processedMember.calendarType;
        delete processedMember.usePrimaryAddress;

        return processedMember;
      });

      // 自動填充虛歲等資訊
      const finalData = autoFillDates(submissionData);
      
      await dispatch(registerQueue(finalData)).unwrap();
      
      if (!embedded) {
        dispatch(showAlert({
          message: '候位登記成功！',
          severity: 'success'
        }));
      }
    } catch (error) {
      console.error('註冊失敗:', error);
      dispatch(showAlert({
        message: `註冊失敗: ${error}`,
        severity: 'error'
      }));
    }
  }, [formData, validateForm, dispatch, embedded]);

  // 返回首頁
  const handleBackToHome = useCallback(() => {
    if (embedded) {
      // 如果是嵌入模式，只重置狀態
      dispatch(resetRegistration());
      setFormData(initialFormData);
      setShowSuccessPage(false);
      setFormErrors({});
    } else {
      // 如果不是嵌入模式，導航到首頁
      dispatch(resetRegistration());
      navigate('/');
    }
  }, [embedded, dispatch, navigate]);

  // 處理家人地址"同上"功能
  const handleUsePrimaryAddress = useCallback((index, checked) => {
    if (checked && formData.addresses[0]?.address) {
      // 複製主客戶的第一個地址
      const newFamilyMembers = [...formData.familyMembers];
      newFamilyMembers[index] = {
        ...newFamilyMembers[index],
        address: formData.addresses[0].address,
        addressType: formData.addresses[0].addressType,
        usePrimaryAddress: true
      };
      setFormData({ ...formData, familyMembers: newFamilyMembers });
    } else {
      // 取消勾選時，只更新勾選狀態，保留地址內容
      const newFamilyMembers = [...formData.familyMembers];
      newFamilyMembers[index] = {
        ...newFamilyMembers[index],
        usePrimaryAddress: false
      };
      setFormData({ ...formData, familyMembers: newFamilyMembers });
    }
  }, [formData]);

  return {
    // 狀態
    formData,
    formErrors,
    showSuccessPage,
    isLoading,
    registeredQueueNumber,
    waitingCount,
    estimatedWaitTime,
    estimatedEndTime,
    maxOrderIndex,
    maxOrderMessage,
    registeredOrderIndex,
    error,

    // 方法
    handleChange,
    handleAddressChange,
    addAddress,
    removeAddress,
    handleFamilyMemberChange,
    handleUsePrimaryAddress,
    addFamilyMember,
    removeFamilyMember,
    handleSubmit,
    handleBackToHome,
    validateForm
  };
};
